import { useEffect, useMemo, useState } from "react";
import { useData } from "@/contexts/DataContext";
import { getGmpPercentage } from "@/lib/gmpUtils";
import { formatSignedInr } from "@/lib/numberFormat";
import { toApiDate, toDisplayDateInput } from "@/lib/dateFormat";
import { Search } from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, ComposedChart,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

type ChartType = "line" | "bar" | "candle";

const CandlestickBar = (props: any) => {
  const { x, y, width, height, payload } = props;
  if (!payload) return null;
  const { open, close } = payload;
  const isUp = close >= open;
  const color = isUp ? "hsl(152, 60%, 40%)" : "hsl(0, 72%, 51%)";
  return <rect x={x} y={y} width={width} height={Math.max(height, 2)} fill={color} rx={2} />;
};

const normalizeStatus = (status?: string) => String(status || "").trim().toLowerCase();

const formatAxisTick = (value: number) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";

  const normalized = Math.abs(numeric) < 0.000001 ? 0 : numeric;
  const rounded = Number(normalized.toFixed(1));
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
};

const AdminGmp = () => {
  const { ipos, updateIpo, gmpHistory, addGmpEntry } = useData();
  const nonListedIpos = useMemo(
    () =>
      ipos.filter((ipo) => {
        const status = normalizeStatus(ipo.status);
        return status !== "listed" && status !== "closed";
      }),
    [ipos]
  );
  const [selectedIpo, setSelectedIpo] = useState(nonListedIpos[0]?.id || "");
  const [newGmp, setNewGmp] = useState("");
  const [newDate, setNewDate] = useState(toDisplayDateInput(new Date().toISOString().split("T")[0]));
  const [chartType, setChartType] = useState<ChartType>("line");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const toSortableTime = (value?: string | null) => {
    const normalized = toApiDate(value);
    const timestamp = Date.parse(normalized || "");
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const sortedNonListedIpos = [...nonListedIpos].sort(
    (a, b) => toSortableTime(b.openDate) - toSortableTime(a.openDate)
  );
  const filteredQuickUpdateIpos = !normalizedSearch
    ? sortedNonListedIpos
    : sortedNonListedIpos.filter((ipo) =>
        [
          ipo.company,
          ipo.status,
          ipo.priceBand,
          ipo.openDate,
          ipo.closeDate,
          ipo.subscriptionStatus,
          ipo.exchange,
        ]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch)
      );
  const totalPages = Math.max(1, Math.ceil(filteredQuickUpdateIpos.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedQuickUpdateIpos = filteredQuickUpdateIpos.slice((safePage - 1) * pageSize, safePage * pageSize);

  useEffect(() => {
    if (!nonListedIpos.length) {
      setSelectedIpo("");
      return;
    }

    if (!selectedIpo || !nonListedIpos.some((ipo) => ipo.id === selectedIpo)) {
      setSelectedIpo(nonListedIpos[0].id);
    }
  }, [nonListedIpos, selectedIpo]);

  const handleUpdateGmp = (ipoId: string, gmp: number) => {
    const ipo = ipos.find((i) => i.id === ipoId);
    if (!ipo) return;
    // updateIpo now auto-adds GMP history entry and keeps yesterday baseline stable.
    updateIpo(ipoId, { gmp });
    setNewGmp("");
  };

  const selectedHistory = gmpHistory[selectedIpo] || [];
  const selectedIpoData = nonListedIpos.find((i) => i.id === selectedIpo);

  const candleData = selectedHistory.map((entry, i) => ({
    ...entry,
    open: i > 0 ? selectedHistory[i - 1].gmp : entry.gmp,
    close: entry.gmp,
  }));
  const onSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  return (
    <div>
      <h1 className="text-2xl font-display font-bold text-foreground mb-6">GMP Manager</h1>
      <form onSubmit={onSearchSubmit} className="mb-6 flex flex-col sm:flex-row gap-2 sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search IPO by company, status, date..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
          />
        </div>
        <button
          type="submit"
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          <Search size={15} />
          Search
        </button>
      </form>

      {/* Quick Update All */}
      <div className="finance-card p-5 mb-6">
        <h2 className="font-display font-semibold mb-4">Quick GMP Update</h2>
        <p className="text-xs text-muted-foreground mb-3">When you update GMP, the graph automatically records the new value with today's date.</p>
        <div className="space-y-3">
          {paginatedQuickUpdateIpos.map((ipo) => {
            const pct = getGmpPercentage(ipo.gmp, ipo.priceBand);
            return (
              <div key={ipo.id} className="flex items-center gap-4 flex-wrap">
                <span className="text-sm font-medium w-48 shrink-0">{ipo.company}</span>
                <span className="text-sm text-muted-foreground">
                  Current:{" "}
                  <strong className={ipo.gmp >= 0 ? "text-success" : "text-destructive"}>
                    {formatSignedInr(ipo.gmp)}
                  </strong>{" "}
                  <span className="text-xs">({pct}%)</span>
                </span>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="New GMP"
                  className="w-28 px-3 py-1.5 rounded-lg border border-input bg-background text-sm"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const input = e.target as HTMLInputElement;
                      const raw = input.value.trim();
                      if (raw === "") return;
                      const val = Number(raw);
                      if (Number.isFinite(val)) {
                        handleUpdateGmp(ipo.id, val);
                      }
                      input.value = "";
                    }
                  }}
                />
                <button
                  onClick={(e) => {
                    const input = (e.currentTarget.previousElementSibling as HTMLInputElement);
                    const raw = input.value.trim();
                    if (raw === "") return;
                    const val = Number(raw);
                    if (Number.isFinite(val)) {
                      handleUpdateGmp(ipo.id, val);
                      input.value = "";
                    }
                  }}
                  className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium"
                >
                  Update
                </button>
              </div>
            );
          })}
        </div>
        {filteredQuickUpdateIpos.length === 0 && (
          <div className="text-center py-4 text-sm text-muted-foreground">No IPOs found for quick update.</div>
        )}
        {filteredQuickUpdateIpos.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredQuickUpdateIpos.length)} of {filteredQuickUpdateIpos.length}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* GMP Graph */}
      <div className="finance-card p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display font-semibold">GMP Trend Graph</h2>
          <div className="flex gap-1">
            {(["line", "bar", "candle"] as ChartType[]).map((t) => (
              <button
                key={t}
                onClick={() => setChartType(t)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                  chartType === t ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:text-foreground"
                }`}
              >
                {t === "candle" ? "Candle" : t.charAt(0).toUpperCase() + t.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-4">
          <select
            value={selectedIpo}
            onChange={(e) => setSelectedIpo(e.target.value)}
            className="px-3 py-2 rounded-lg border border-input bg-background text-sm"
          >
            {nonListedIpos.map((ipo) => (
              <option key={ipo.id} value={ipo.id}>{ipo.company}</option>
            ))}
          </select>
        </div>

        {selectedHistory.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            {chartType === "line" ? (
              <LineChart data={selectedHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" tickFormatter={formatAxisTick} width={48} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                <Line type="monotone" dataKey="gmp" stroke="hsl(var(--secondary))" strokeWidth={2} dot={{ fill: "hsl(var(--secondary))" }} />
              </LineChart>
            ) : chartType === "bar" ? (
              <BarChart data={selectedHistory}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" tickFormatter={formatAxisTick} width={48} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                <Bar dataKey="gmp" radius={[4, 4, 0, 0]}>
                  {selectedHistory.map((entry, idx) => (
                    <Cell key={idx} fill={idx > 0 && entry.gmp >= selectedHistory[idx - 1].gmp ? "hsl(152, 60%, 40%)" : idx === 0 ? "hsl(var(--secondary))" : "hsl(0, 72%, 51%)"} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <ComposedChart data={candleData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="date" fontSize={12} stroke="hsl(var(--muted-foreground))" />
                <YAxis fontSize={12} stroke="hsl(var(--muted-foreground))" tickFormatter={formatAxisTick} width={48} />
                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                <Bar dataKey="gmp" shape={<CandlestickBar />} />
              </ComposedChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="text-center py-12 text-muted-foreground text-sm">
            No GMP history for {selectedIpoData?.company || "this IPO"}. Update GMP values to start tracking trends.
          </div>
        )}

        {/* Add historical entry */}
        <div className="mt-4 flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Date</label>
            <input
              type="text"
              inputMode="numeric"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              placeholder="dd-mm-yyyy"
              className="px-3 py-1.5 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">GMP Value</label>
            <input type="text" inputMode="decimal" value={newGmp} onChange={(e) => setNewGmp(e.target.value)} placeholder="₹" className="w-28 px-3 py-1.5 rounded-lg border border-input bg-background text-sm" />
          </div>
          <button
            onClick={() => {
              const raw = newGmp.trim();
              const entryDate = toApiDate(newDate);
              if (selectedIpo && raw !== "" && entryDate) {
                const value = Number(raw);
                if (!Number.isFinite(value)) {
                  return;
                }
                addGmpEntry(selectedIpo, { date: entryDate, gmp: value });
                setNewGmp("");
              }
            }}
            className="px-4 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-sm font-medium"
          >
            Add to Graph
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminGmp;
